import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

/**
 * Group Session Service - Handles all group session related API calls
 */
class GroupSessionService {
  /**
   * Get auth headers with token
   * @returns {Object} Headers with authorization token
   */
  static getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
      headers: { Authorization: `Bearer ${token}` }
    };
  }

  /**
   * Create a new group session (counselors only)
   * @param {Object} sessionData - Session data
   * @returns {Promise} API response with created session
   */
  static async createGroupSession(sessionData) {
    try {
      const response = await axios.post(
        `${API_URL}/group-sessions/create`,
        sessionData,
        this.getAuthHeaders()
      );

      return response.data;
    } catch (error) {
      console.error('Error creating group session:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Get all upcoming group sessions
   * @param {Object} filters - Optional filters (category, targetAudience, etc.)
   * @returns {Promise} API response with list of sessions
   */
  static async getUpcomingSessions(filters = {}) {
    try {
      const response = await axios.get(
        `${API_URL}/group-sessions/upcoming`,
        {
          ...this.getAuthHeaders(),
          params: filters
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching upcoming sessions:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Get group session by ID
   * @param {string} sessionId - Session ID
   * @returns {Promise} API response with session details
   */
  static async getSessionById(sessionId) {
    try {
      const response = await axios.get(
        `${API_URL}/group-sessions/${sessionId}`,
        this.getAuthHeaders()
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching session details:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Register for a group session
   * @param {string} sessionId - Session ID
   * @returns {Promise} API response with registration confirmation
   */
  static async registerForSession(sessionId) {
    try {
      const response = await axios.post(
        `${API_URL}/group-sessions/${sessionId}/register`,
        {},
        this.getAuthHeaders()
      );

      return response.data;
    } catch (error) {
      console.error('Error registering for session:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Cancel registration for a group session
   * DELETE /group-sessions/:sessionId/register (protected)
   * @param {string} sessionId - Session ID
   * @returns {Promise} API response
   */
  static async cancelRegistration(sessionId) {
    try {
      const response = await axios.delete(
        `${API_URL}/group-sessions/${sessionId}/register`,
        this.getAuthHeaders()
      );

      return response.data;
    } catch (error) {
      console.error('Error cancelling registration:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Submit feedback for a session
   * POST /group-sessions/:sessionId/feedback (protected)
   * @param {string} sessionId - Session ID
   * @param {Object} feedback - Feedback data (rating, comment)
   * @returns {Promise} API response
   */
  static async submitFeedback(sessionId, feedback) {
    try {
      const response = await axios.post(
        `${API_URL}/group-sessions/${sessionId}/feedback`,
        feedback,
        this.getAuthHeaders()
      );

      return response.data;
    } catch (error) {
      console.error('Error submitting feedback:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Get user's registered sessions
   * GET /group-sessions/my-sessions (protected)
   * @returns {Promise} API response with user's sessions
   */
  static async getMySessions() {
    try {
      const response = await axios.get(
        `${API_URL}/group-sessions/my-sessions`,
        this.getAuthHeaders()
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching user sessions:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Get sessions facilitated by current counselor
   * GET /group-sessions/facilitator/sessions (protected - counselor/admin/hod only)
   * @returns {Promise} API response with facilitated sessions
   */
  static async getFacilitatedSessions() {
    try {
      const response = await axios.get(
        `${API_URL}/group-sessions/facilitator/sessions`,
        this.getAuthHeaders()
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching facilitated sessions:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Update group session details (facilitator only)
   * PUT /group-sessions/:sessionId (protected - counselor/admin only)
   * @param {string} sessionId - Session ID
   * @param {Object} updates - Updated session data
   * @returns {Promise} API response with updated session
   */
  static async updateSession(sessionId, updates) {
    try {
      const response = await axios.put(
        `${API_URL}/group-sessions/${sessionId}`,
        updates,
        this.getAuthHeaders()
      );

      return response.data;
    } catch (error) {
      console.error('Error updating session:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Delete/cancel a group session (admin/facilitator only)
   * DELETE /group-sessions/:sessionId (protected - counselor/admin only)
   * @param {string} sessionId - Session ID
   * @returns {Promise} API response
   */
  static async deleteSession(sessionId) {
    try {
      const response = await axios.delete(
        `${API_URL}/group-sessions/${sessionId}`,
        this.getAuthHeaders()
      );

      return response.data;
    } catch (error) {
      console.error('Error deleting session:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Mark participant as attended
   * POST /group-sessions/:sessionId/attendance (protected - counselor/admin only)
   * @param {string} sessionId - Session ID
   * @param {Object} attendanceData - Attendance data with participantId and attended status
   * @returns {Promise} API response
   */
  static async markAttendance(sessionId, attendanceData) {
    try {
      const response = await axios.post(
        `${API_URL}/group-sessions/${sessionId}/attendance`,
        attendanceData,
        this.getAuthHeaders()
      );

      return response.data;
    } catch (error) {
      console.error('Error marking attendance:', error);
      throw error.response?.data || error;
    }
  }

  /**
   * Complete session with notes (facilitator only)
   * POST /group-sessions/:sessionId/complete (protected - counselor/admin only)
   * @param {string} sessionId - Session ID
   * @param {Object} completionData - Session completion notes
   * @returns {Promise} API response
   */
  static async completeSession(sessionId, completionData) {
    try {
      const response = await axios.post(
        `${API_URL}/group-sessions/${sessionId}/complete`,
        completionData,
        this.getAuthHeaders()
      );

      return response.data;
    } catch (error) {
      console.error('Error completing session:', error);
      throw error.response?.data || error;
    }
  }
}

export default GroupSessionService;
